if (!customElements.get('onehalf-tabs')) {
  customElements.define(
    'onehalf-tabs',
    class OnehalfTabs extends HTMLElement {
      connectedCallback() {
        this.tablist = this.querySelector('[role="tablist"]');
        this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));

        this.tabs.forEach((tab) => {
          tab.addEventListener('click', () => this.select(tab));
          tab.addEventListener('keydown', (event) => this.onKeydown(event));
        });

        this.sliders = Array.from(this.querySelectorAll('.oh-collections__panel'))
          .map((panel) => this.setupSlider(panel))
          .filter(Boolean);

        this.onResize = () => this.sliders.forEach((slider) => slider.update());
        window.addEventListener('resize', this.onResize, { passive: true });

        this.onBlockSelect = this.onBlockSelect.bind(this);
        document.addEventListener('shopify:block:select', this.onBlockSelect);
      }

      disconnectedCallback() {
        window.removeEventListener('resize', this.onResize);
        document.removeEventListener('shopify:block:select', this.onBlockSelect);
      }

      setupSlider(panel) {
        const grid = panel.querySelector('.oh-collections__grid');
        const controls = panel.querySelector('.oh-collections__controls');
        if (!grid || !controls) return null;

        const bar = controls.querySelector('.oh-collections__progress-bar');
        const buttons = Array.from(controls.querySelectorAll('.oh-collections__nav-button'));
        const isRtl = () => getComputedStyle(grid).direction === 'rtl';

        const update = () => {
          const max = grid.scrollWidth - grid.clientWidth;
          controls.hidden = max <= 1;
          if (max <= 1) return;

          const ratio = Math.min(grid.clientWidth / grid.scrollWidth, 1);
          const position = Math.min(Math.abs(grid.scrollLeft), max);
          const progress = position / max;
          const direction = isRtl() ? -1 : 1;

          bar.style.width = `${ratio * 100}%`;
          bar.style.transform = `translateX(${direction * progress * (1 / ratio - 1) * 100}%)`;

          buttons.forEach((button) => {
            const step = Number(button.dataset.direction);
            button.disabled = step < 0 ? position <= 1 : position >= max - 1;
          });
        };

        buttons.forEach((button) => {
          button.addEventListener('click', () => {
            const item = grid.querySelector('.oh-collections__item');
            const gap = parseFloat(getComputedStyle(grid).columnGap) || 0;
            const distance = item ? item.getBoundingClientRect().width + gap : grid.clientWidth;
            const direction = Number(button.dataset.direction) * (isRtl() ? -1 : 1);
            grid.scrollBy({ left: distance * direction, behavior: 'smooth' });
          });
        });

        grid.addEventListener('scroll', () => requestAnimationFrame(update), { passive: true });
        update();

        return { panel, update };
      }

      select(tab, focus = false) {
        this.tabs.forEach((item, index) => {
          const selected = item === tab;
          const panel = document.getElementById(item.getAttribute('aria-controls'));
          item.setAttribute('aria-selected', selected);
          item.tabIndex = selected ? 0 : -1;
          if (selected && this.tablist) this.tablist.dataset.active = index;
          if (!panel) return;
          panel.hidden = !selected;
          if (selected) {
            panel.classList.remove('oh-collections__panel--enter');
            void panel.offsetWidth;
            panel.classList.add('oh-collections__panel--enter');
            const slider = this.sliders.find((entry) => entry.panel === panel);
            if (slider) slider.update();
          }
        });
        if (focus) tab.focus();
      }

      onKeydown(event) {
        const index = this.tabs.indexOf(event.currentTarget);
        let next;
        if (event.key === 'ArrowRight') next = this.tabs[(index + 1) % this.tabs.length];
        if (event.key === 'ArrowLeft') next = this.tabs[(index - 1 + this.tabs.length) % this.tabs.length];
        if (event.key === 'Home') next = this.tabs[0];
        if (event.key === 'End') next = this.tabs[this.tabs.length - 1];
        if (!next) return;
        event.preventDefault();
        this.select(next, true);
      }

      onBlockSelect(event) {
        if (!this.contains(event.target)) return;
        const panel = event.target.closest('[role="tabpanel"]');
        if (panel && panel.hidden) {
          const tab = this.tabs.find((item) => item.getAttribute('aria-controls') === panel.id);
          if (tab) this.select(tab);
        }
        event.target.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      }
    }
  );
}
